import React, { createContext, useContext, useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

interface AuthCtx {
  isLoggedIn: boolean;
  loading: boolean;
  adminEmail: string | null;
  // Başarılı + yetkili → null döner; aksi halde kullanıcıya gösterilecek hata mesajı.
  login: (email: string, pass: string) => Promise<string | null>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthCtx>({
  isLoggedIn: false,
  loading: true,
  adminEmail: null,
  login: async () => "Auth hazır değil.",
  logout: async () => {},
});

// Verilen kullanıcının admins/{uid} dökümanı var mı? (yönetici yetkisi)
async function isAdminUser(user: User): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, "admins", user.uid));
    return snap.exists();
  } catch {
    return false;
  }
}

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminEmail, setAdminEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Oturum durumunu izle — sayfa yenilenince admin girişi korunur,
  // ama her seferinde admins/{uid} kontrolü tekrar yapılır.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user && (await isAdminUser(user))) {
        setIsLoggedIn(true);
        setAdminEmail(user.email);
      } else {
        setIsLoggedIn(false);
        setAdminEmail(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = async (email: string, pass: string): Promise<string | null> => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, pass);
      const admin = await isAdminUser(cred.user);
      if (!admin) {
        await signOut(auth);
        return "Bu hesabın yönetici yetkisi yok.";
      }
      // onAuthStateChanged isLoggedIn/adminEmail'i set edecek.
      return null;
    } catch {
      return "E-posta veya şifre hatalı.";
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, loading, adminEmail, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAdminAuth = () => useContext(AuthContext);
